import type { Container } from "pixi.js";
import type { IntroScene } from "../../intro/IntroScene";
import type { IntroSplashPort } from "../../intro/IntroSplashPort";
import type { CoreServices } from "../CoreServices";

/** A title's intro as the boot opens and closes it with the splash (decision 77). */
export interface TitleIntroStage {
  /** It shows: mounted in the intro layer, the game's scene hidden, its motion started. */
  open(): void;
  /** It goes (opened or not): unmounted and disposed, the game's scene shown. */
  close(): void;
}

/**
 * `intro` on the stage: in the intro layer while it shows, the game's scene hidden behind it — the scene's node only, so
 * the background still beside it in the game layer (decision 69) stays under the intro (decision 77).
 */
export function stageTitleIntro(intro: IntroScene, nodes: { scene: Container; layer: Container }): TitleIntroStage {
  return {
    open: () => {
      nodes.scene.visible = false;
      nodes.layer.addChild(intro.node);
      intro.start();
    },
    close: () => {
      intro.node.removeFromParent();
      intro.dispose();
      nodes.scene.visible = true;
    },
  };
}

/**
 * The splash takes over from the loader and holds the game until the first gesture, which also
 * unlocks WebAudio. `skipIntro` (dev builds without `?intro=1`) hides it and asks for the unlock at once — a
 * dev-only convenience, so the offline e2e never waits for a tap. With no gesture behind that call the
 * browser may refuse it; the audio service then unlocks on the player's first tap or key anywhere.
 * No exit clip ships: the outro is a beat on the chrome runner (100 ms on a replay boot, else none).
 * A title's intro (decision 77) shows with the splash and goes with it; a skipped intro only disposes it.
 */
export async function runIntro(
  panel: IntroSplashPort,
  services: Pick<CoreServices, "audioService" | "chromeTaskRunner">,
  input: { skipIntro: boolean; fastIntro: boolean; dismissLoader: () => void; titleIntro?: TitleIntroStage | null },
): Promise<void> {
  const titleIntro = input.titleIntro ?? null;
  if (input.skipIntro) {
    panel.hide();
    titleIntro?.close();
    services.audioService.control.unlockFromUserGesture();
    return;
  }
  titleIntro?.open();
  panel.show();
  input.dismissLoader();
  await panel.waitForFirstGesture();
  services.audioService.control.unlockFromUserGesture();
  await services.chromeTaskRunner.sleep(input.fastIntro ? 0.1 : 0);
  panel.hide();
  titleIntro?.close();
}
